import {
  CoverageWithPricesInfo,
  crmClient,
  eligibilityClient,
  FeatureManager,
  FunnelErrors,
  getHintPlanIdForMembershipType,
  hintClient,
  isMaintainMembership,
  isTechMembership,
  LeadOnboardingStage,
  MembershipType,
  MixpanelClient,
  onboardingClient,
  PaymentOption,
  reportMonitoredError,
  StatsigEventName,
  StatsigManager,
  STRIPE_PUBLIC_KEY,
  userClient,
} from '@enaratech/funnel-helper';
import { Grid, Stack, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Agreements, { RefAgreements } from 'src/components/Common/Agreements/Agreements';
import { Page, withProgress } from 'src/components/Common/Progress/Progress';
import Toast from 'src/components/Common/Toast/Toast';
import BasicLayout from 'src/components/Layout/BasicLayout/BasicLayout';
import { useAuth } from 'src/contexts/auth';
import { SET_HINT_PATIENT_ID } from 'src/contexts/auth/types';
import { useClinic } from 'src/contexts/clinic';
import { useExperiment } from 'src/contexts/experiments';
import { useOnboarding } from 'src/contexts/onboarding';
import { PaymentMode, useMembershipStore } from 'src/store/memberships';
import { getEffectiveProgramType } from 'src/utils/programType';
import { navigateToPage } from '../routes';
import CoverageResult from './CoverageResult';
import { inputFields, PLAN_OPTIONS } from './Inputs/inputFields';
import { PaymentForm, PaymentMethodResult } from './Inputs/Payment.types';
import PaymentInputs from './Inputs/PaymentInputs';
import './scss/payments.scss';

// Product decision: a failed card capture must not block onboarding — the care
// team collects the card manually later. The patient (or whoever is assisting
// them) still needs to know, in plain terms and without technical details.
const CARD_CAPTURE_FAILED_MESSAGE =
  "We couldn't save your card details right now — you can still continue. " +
  'Our care team will reach out to help you finish this step.';

const ENROLLMENT_FAILED_MESSAGE =
  "We couldn't finish setting up your membership. Please try again, " +
  'or contact our care team if it keeps happening.';

const notifyCardCaptureFailed = () => {
  Toast.notification('info', CARD_CAPTURE_FAILED_MESSAGE, {
    autoClose: false,
    closeOnClick: true,
    draggable: true,
  });
};

// Fixed message per step so Sentry groups events by step, with opaque ids in
// extras. Never put patient data (name, email, phone) here — HIPAA.
const reportPaymentStepFailure = (step: string, extra: Record<string, unknown>) => {
  Sentry.captureMessage(`Onboarding payment: ${step} failed`, {
    level: 'error',
    extra,
  });
};

const Payment: FC = () => {
  const [canContinue, setCanContinue] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [stripePromise] = useState(() => loadStripe(STRIPE_PUBLIC_KEY));
  const [stripePaymentMethod, setStripePaymentMethod] = useState<PaymentMethodResult | undefined>(
    undefined
  );
  const [coverage, setCoverage] = useState<CoverageWithPricesInfo | null>(null);
  const [agreementsLoaded, setAgreementsLoaded] = useState<boolean>(false);
  const [allAgreementsSelected, setAllAgreementsSelected] = useState<boolean>(false);

  const agreementsRef = useRef<RefAgreements>(null);
  const navigate = useNavigate();

  const planDuration = useMembershipStore((s) => s.duration);
  const isMonthly = useMembershipStore((s) => s.isMonthly);
  const updatePaymentMode = useMembershipStore((s) => s.updatePaymentMode);

  const {
    authState: { user, isReferral },
    dispatchAuth,
  } = useAuth();

  const {
    onboardingState: {
      eligibility: { membershipType },
      magicLink,
      locationFlow,
    },
  } = useOnboarding();

  const { clinicState } = useClinic();

  // Experiment facing changes
  const {
    experimentState: { isInitialized },
  } = useExperiment();

  const isArrivingFromAdwords = useMemo(() => {
    if (!isInitialized) {
      return false;
    }

    return StatsigManager.isArrivingFromAdwords();
  }, [isInitialized]);

  const [formState, setFormState] = useState<PaymentForm>({
    street: '',
    city: clinicState!.patient.city ?? '',
    state: clinicState!.patient.state ?? '',
    zipCode: user!.zipCode ?? '',
  });

  const hint = clinicState?.hint;

  const options = {
    fonts: [
      {
        cssSrc: 'https://fonts.googleapis.com/css2?family=Proxima+Nova',
      },
    ],
  };

  const getPaymentMethod = (paymentMethod: PaymentMethodResult) => {
    setStripePaymentMethod(paymentMethod);
  };

  const getInitialPrice = () => {
    if (!coverage) {
      return '';
    }

    return isMonthly ? `${coverage.originalPrices.monthly}` : `${coverage.originalPrices.annual}`;
  };

  const getDiscountPrice = () => {
    return isMonthly
      ? `${coverage?.discountsApplied.monthly}`
      : `${coverage?.discountsApplied.annual}`;
  };

  const shouldGoToSSBFlow = async (): Promise<boolean> => {
    if (
      (locationFlow && FeatureManager.shouldForceGoToSSBForReferralHubFlow(locationFlow)) ||
      magicLink ||
      (await FeatureManager.shouldReferralGoThroughSSB({ isReferral, clinicId: user!.clinicId }))
    ) {
      return true;
    }

    return await FeatureManager.isSelfServeBookingFeatureEnabled(
      clinicState!.details.clinicId,
      user!.insuranceCompany
    );
  };

  const handleSubmit = async () => {
    const { firstName, lastName, email } = user!;

    const IDs: {
      patient: string | null;
      membership: string | null;
      card: string | null;
      techPatient: string | null;
      techMembership: string | null;
      techCard: string | null;
    } = {
      patient: null,
      membership: null,
      card: null,
      techPatient: null,
      techMembership: null,
      techCard: null,
    };

    if (stripePaymentMethod?.error) {
      return;
    }

    setIsSubmitting(true);

    const isTech = isTechMembership(membershipType);

    try {
      const patient = await hintClient.createPatient({
        email,
        first_name: firstName,
        last_name: lastName,
        lead_source_id: hint!.leadSource,
        location_id: hint!.location,
        practitioner_id: hint!.supervisingMd,
        hint_code: hint!.hintCode,
      });

      IDs.patient = patient.id;

      if (!isTech) {
        const card = await hintClient.createPaymentMethod({
          patient_id: patient.id,
          stripe_id: stripePaymentMethod!.stripePaymentMethod.id,
          hint_code: hint!.hintCode,
        });

        IDs.card = card?.id ?? null;

        if (!card) {
          notifyCardCaptureFailed();
          reportPaymentStepFailure('card capture', {
            hint_code: hint!.hintCode,
            hint_patient_id: patient.id,
          });
        }
      }

      const membership = await hintClient.createMembership({
        patient_id: patient.id,
        plan_id: getHintPlanIdForMembershipType({
          clinic: clinicState!,
          membershipType: membershipType!,
          insuranceOutcome: user!.insuranceOutcome,
        }),
        period_in_months: planDuration,
        hint_code: hint!.hintCode,
      });

      IDs.membership = membership?.id ?? null;

      if (!membership) {
        reportPaymentStepFailure('membership creation', {
          hint_code: hint!.hintCode,
          hint_patient_id: patient.id,
        });
      }

      if (isTech) {
        const techPatient = await hintClient.createPatient({
          email,
          first_name: firstName,
          last_name: lastName,
          lead_source_id: hint!.otherSubscription!.leadSource,
          location_id: hint!.otherSubscription!.location,
          practitioner_id: hint!.otherSubscription!.supervisingMd,
          hint_code: hint!.otherSubscription!.hintCode,
        });

        IDs.techPatient = techPatient.id;

        const [techCard, techMembership] = await Promise.all([
          hintClient.createPaymentMethod({
            patient_id: techPatient.id,
            stripe_id: stripePaymentMethod!.stripePaymentMethod.id,
            hint_code: hint!.otherSubscription!.hintCode,
          }),
          hintClient.createMembership({
            patient_id: techPatient.id,
            plan_id: getHintPlanIdForMembershipType({
              clinic: clinicState!,
              membershipType: membershipType!,
              isAppSubscription: true,
              insuranceOutcome: user!.insuranceOutcome,
            }),
            period_in_months: planDuration,
            hint_code: hint!.otherSubscription!.hintCode,
          }),
        ]);

        IDs.techCard = techCard?.id ?? null;
        IDs.techMembership = techMembership?.id ?? null;

        if (!techCard) {
          notifyCardCaptureFailed();
          reportPaymentStepFailure('card capture', {
            hint_code: hint!.otherSubscription!.hintCode,
            hint_patient_id: techPatient.id,
            is_app_subscription: true,
          });
        }

        if (!techMembership) {
          reportPaymentStepFailure('membership creation', {
            hint_code: hint!.otherSubscription!.hintCode,
            hint_patient_id: techPatient.id,
            is_app_subscription: true,
          });
        }
      }

      const paymentOption: PaymentOption = isMonthly ? 'monthly' : 'yearly';

      crmClient.updateLead({
        uuid: user!.uuid,
        shownPrice: getInitialPrice(),
        finalPrice: coverage?.coupon ? getDiscountPrice() : getInitialPrice(),
        billingFrequency: paymentOption,
        couponCode: coverage?.coupon?.code,
      });

      userClient.updateUser({
        address: formState.street,
        city: formState.city,
        state: formState.state,
        zipCode: formState.zipCode,
        hintId: IDs.patient,
      }).then((updated) => {
        // Losing this write means core has no record of the Hint patient id.
        if (!updated) {
          reportPaymentStepFailure('hint patient id persistence', {
            hint_code: hint!.hintCode,
            hint_patient_id: IDs.patient,
          });
        }
      });

      dispatchAuth({ type: SET_HINT_PATIENT_ID, payload: IDs.patient });

      if (agreementsRef.current) {
        agreementsRef.current.signAgreements();
      }

      MixpanelClient.setMetadata({ paymentOption });

      onboardingClient.updateMemberStatus({
        onboardingStage: LeadOnboardingStage.Payment,
      });

      if (await shouldGoToSSBFlow()) {
        navigateToPage({ targetPage: '/appointment/self-serve-booking', navigate });
      } else if (isArrivingFromAdwords) {
        // Members coming from an Adwords campaign should be redirected to the congrats
        // screen instead Member Experience Booking since they already scheduled an
        // appointment for Question Talk
        StatsigManager.logEvent({
          eventName: StatsigEventName.CongratulationScreenDisplayed,
          metadata: {
            memberId: `${user!.id}`,
            memberUuid: user!.uuid,
          },
        });
        return navigateToPage({ targetPage: '/onboarding-call-result', navigate });
      } else if (user!.initialAppointmentId) {
        // Members coming from the legacy funnel might have already booked with MS
        navigateToPage({ targetPage: '/appointment/ms-booking/result', navigate });
      } else {
        navigateToPage({ targetPage: '/appointment/ms-booking', navigate });
      }
    } catch (error) {
      // Known funnel errors are already reported to Sentry inside funnel-helper;
      // anything else thrown in this block used to be shown to the patient
      // verbatim and never reported.
      if (!Object.values(FunnelErrors).includes((error as Error)?.name as FunnelErrors)) {
        reportMonitoredError({ error });
      }

      Toast.notification('error', ENROLLMENT_FAILED_MESSAGE);

      await hintClient.rollbackHintSetup({
        common: {
          hintCode: hint!.hintCode,
          patientId: IDs.patient,
          membershipId: IDs.membership,
          cardId: IDs.card,
        },
        appSubscription: {
          hintCode: hint?.otherSubscription?.hintCode ?? null,
          patientId: IDs.techPatient,
          membershipId: IDs.techMembership,
          cardId: IDs.techCard,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGetCoverage = async () => {
    const coverageResponse = await eligibilityClient.getCoverage({
      membershipType: membershipType ?? MembershipType.Private,
      programType: getEffectiveProgramType({ user, clinic: clinicState })!,
      clinicId: user!.clinicId,
    });

    if (!coverageResponse?.success) {
      Toast.notification('error', 'There was an error getting coverage');
      return;
    }

    setCoverage(coverageResponse as CoverageWithPricesInfo);
  };

  useEffect(() => {
    // Enara Maintain is month-to-month only, and the annual option is never offered for
    // it. Pin the store anyway: `paymentMode` is global and outlives the page, and it
    // decides `period_in_months` on the Hint membership below.
    if (isMaintainMembership(membershipType)) {
      updatePaymentMode(PaymentMode.Monthly);
    }

    handleGetCoverage();
  }, []);

  return (
    <BasicLayout
      title={'Your personalized journey is about to get started!'}
      loading={!coverage}
      buttonProps={{
        text: 'Book my First Appointment',
        disabled:
          !canContinue ||
          !agreementsLoaded ||
          !allAgreementsSelected ||
          Object.values(formState).every((i) => i!.length === 0),
        loading: isSubmitting,
        onClick: handleSubmit,
      }}
      className='container-payment'
      back>
      <Grid container justifyContent={'space-around'} direction={'row-reverse'}>
        <Grid item md={12} lg={5} xl={4}>
          {coverage && (
            <CoverageResult
              planOptions={PLAN_OPTIONS}
              initialCoverage={coverage}
              onCoverageChange={(coverage: CoverageWithPricesInfo | null) => setCoverage(coverage)}
            />
          )}
        </Grid>
        <Grid item md={12} lg={6} xl={7}>
          <Stack>
            <Typography variant={'h4'}>Payment Method</Typography>
            <Typography variant={'body2'} style={{ lineHeight: '28px' }}>
              You will only be charged after your first appointment with your care team.
            </Typography>
            <Elements stripe={stripePromise} options={options}>
              <PaymentInputs
                patient={`${user?.firstName} ${user?.lastName}`}
                inputs={inputFields}
                setCanContinue={setCanContinue}
                form={formState}
                setForm={setFormState}
                getPaymentMethod={getPaymentMethod}
              />
            </Elements>

            <Agreements
              ref={agreementsRef}
              agreementsMode={'needs-auth'}
              address={formState.street}
              onLoaded={useCallback(() => setAgreementsLoaded(true), [])}
              onSelect={useCallback((all: boolean) => setAllAgreementsSelected(all), [])}
            />
          </Stack>
        </Grid>
      </Grid>
    </BasicLayout>
  );
};

export default withProgress(Payment, Page.Payment);
